/**
 * Appearance — System / Light / Dark radio picker.
 *
 * Per the iter-84 spec: "Keep simple. Three options." No animation
 * preferences, no reduced-motion toggle (we honor prefers-reduced-motion
 * implicitly via CSS), no font-size dial. The whole point is that vibe
 * lives on /my-vibe, not here.
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Monitor, Sun, Moon, Check } from "lucide-react";
import MobileTopBar from "../components/mobile/MobileTopBar";
import HamburgerSheet from "../components/mobile/HamburgerSheet";
import { useTheme } from "../lib/ThemeContext";

const OPTIONS = [
    { key: "system", label: "system", desc: "follow your device", icon: Monitor },
    { key: "light",  label: "light",  desc: "for the daytime",     icon: Sun },
    { key: "dark",   label: "dark",   desc: "the house default",   icon: Moon },
];

export default function Appearance() {
    const navigate = useNavigate();
    const { mode, setMode } = useTheme() || {};
    const [hamburgerOpen, setHamburgerOpen] = useState(false);

    return (
        <div className="min-h-screen bg-app text-app pb-24">
            <MobileTopBar
                onOpenMenu={() => setHamburgerOpen(true)}
                onOpenNotifications={() => navigate("/me")}
            />
            <HamburgerSheet open={hamburgerOpen} onClose={() => setHamburgerOpen(false)} />

            <div className="max-w-md mx-auto px-5 pt-6">
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="text-[10px] tracking-[0.32em] uppercase text-app-3 mb-3">appearance</div>
                    <h1 className="font-display text-3xl text-app leading-tight">
                        how should it <span className="italic font-serif">feel</span>?
                    </h1>
                    <p className="mt-3 font-serif italic text-app-2 text-sm leading-relaxed">
                        the card is the focus — the chrome stays out of the way either way.
                    </p>
                </motion.div>

                <div className="mt-8 space-y-2.5" data-testid="appearance-options">
                    {OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const active = mode === opt.key;
                        return (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => setMode(opt.key)}
                                data-testid={`appearance-option-${opt.key}`}
                                aria-pressed={active}
                                className={`w-full flex items-center gap-4 px-4 py-4 rounded-2xl border transition-all text-left ${
                                    active
                                        ? "border-amber-300/55 bg-amber-400/8"
                                        : "border-white/10 hover:border-white/25 bg-black/20"
                                }`}
                            >
                                <span className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    active ? "bg-amber-400/15 text-amber-200" : "bg-white/5 text-app-2"
                                }`}>
                                    <Icon className="w-4 h-4" strokeWidth={1.7} />
                                </span>
                                <span className="flex-1">
                                    <div className="font-sans text-sm text-app lowercase tracking-wide">
                                        {opt.label}
                                    </div>
                                    <div className="font-serif italic text-[12px] text-app-3 mt-0.5">
                                        {opt.desc}
                                    </div>
                                </span>
                                <span
                                    className={`w-5 h-5 rounded-full flex items-center justify-center border ${
                                        active ? "bg-amber-400 border-amber-300" : "border-white/20"
                                    }`}
                                    aria-hidden="true"
                                >
                                    {active && <Check className="w-3 h-3 text-black" strokeWidth={2.5} />}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <p className="mt-7 text-center text-[10px] tracking-[0.22em] uppercase text-app-3">
                    you can change this anytime
                </p>
            </div>
        </div>
    );
}
